import React, { createContext, useContext, useReducer, useEffect } from 'react';

interface CartItem {
  productId: string;
  productName: string;
  productCode: string;
  quantity: number;
  price: number;
  unit: string;
  supplier: {
    _id: string;
    name: string;
  };
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: CartItem }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; quantity: number } }
  | { type: 'REMOVE_FROM_CART'; payload: { productId: string } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] };

interface CartContextType {
  state: CartState;
  addToCart: (product: any, quantity?: number) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  toggleCart: () => void;
  getCartTotal: () => number;
  getCartItemsCount: () => number;
  createOrder: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItem = state.items.find(item => item.productId === action.payload.productId);
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.productId === action.payload.productId
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      } else {
        return {
          ...state,
          items: [...state.items, action.payload]
        };
      }
    }
    
    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.productId !== action.payload.productId)
        };
      }
      
      return {
        ...state,
        items: state.items.map(item =>
          item.productId === action.payload.productId
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
    }
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.productId !== action.payload.productId)
      };
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
    
    case 'TOGGLE_CART':
      return {
        ...state,
        isOpen: !state.isOpen
      };
    
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload
      };
    
    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    isOpen: false
  });

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('plannitas-cart');
      if (savedCart) {
        const cartItems = JSON.parse(savedCart);
        dispatch({ type: 'LOAD_CART', payload: cartItems });
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('plannitas-cart', JSON.stringify(state.items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [state.items]);

  const addToCart = (product: any, quantity: number = 1) => {
    const cartItem: CartItem = {
      productId: product._id,
      productName: product.name,
      productCode: product.code,
      quantity,
      price: product.salePrice,
      unit: product.unit,
      supplier: {
        _id: product.supplier._id,
        name: product.supplier.name
      }
    };
    
    dispatch({ type: 'ADD_TO_CART', payload: cartItem });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
  };

  const removeFromCart = (productId: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { productId } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const toggleCart = () => {
    dispatch({ type: 'TOGGLE_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemsCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const createOrder = async () => {
    try {
      // Group items by supplier
      const ordersBySupplier = state.items.reduce((acc, item) => {
        const supplierId = item.supplier._id;
        if (!acc[supplierId]) {
          acc[supplierId] = {
            supplier: item.supplier,
            items: [],
            total: 0
          };
        }
        acc[supplierId].items.push({
          productId: item.productId,
          productName: item.productName,
          productCode: item.productCode,
          quantity: item.quantity,
          price: item.price,
          unit: item.unit,
          subtotal: item.price * item.quantity
        });
        acc[supplierId].total += item.price * item.quantity;
        return acc;
      }, {} as any);

      // Create orders for each supplier
      const orderPromises = Object.values(ordersBySupplier).map(async (order: any) => {
        const response = await fetch('/api/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            supplier: order.supplier._id,
            items: order.items,
            total: order.total,
            status: 'pending',
            orderDate: new Date().toISOString()
          })
        });
        
        if (!response.ok) {
          throw new Error(`Error creating order for ${order.supplier.name}`);
        }
        
        return response.json();
      });

      await Promise.all(orderPromises);
      
      // Clear cart after successful order creation
      clearCart();
      
      alert('¡Pedidos creados exitosamente! Se han generado pedidos separados por proveedor.');
      
    } catch (error) {
      console.error('Error creating orders:', error);
      alert('Error al crear los pedidos. Por favor, inténtalo de nuevo.');
    }
  };

  const value: CartContextType = {
    state,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    toggleCart,
    getCartTotal,
    getCartItemsCount,
    createOrder
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};